import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, CheckCircle2, ArrowRight, X, Lock, Mail, ShieldCheck,
  Download, Clock, Award, FilePlus2, ScrollText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  { icon: ScrollText, title: 'Chiarezza nelle trattative', desc: 'Permette di definire fin da subito prezzo, condizioni e tempistiche, evitando ambiguità ed incomprensioni tra le parti.' },
  { icon: Lock, title: 'Esclusiva temporanea sul velivolo', desc: "Tutela l'acquirente durante la fase di analisi, riducendo il rischio che il velivolo venga proposto a terzi." },
  { icon: ShieldCheck, title: 'Supporto alla due diligence', desc: 'Crea una base formale per avviare ispezioni tecniche e verifiche documentali con maggiore sicurezza e tutela.' },
  { icon: FilePlus2, title: 'Documento professionale', desc: 'Utilizzabile per diverse tipologie di operazioni e personalizzabile in base alle esigenze delle parti.' },
  { icon: Download, title: 'Formato digitale pronto all\'uso', desc: 'PDF scaricabile, compilabile e stampabile, progettato per un utilizzo rapido ed immediato.' },
  { icon: Clock, title: 'Consegna istantanea', desc: 'Il PDF viene recapitato via email subito dopo la conferma del pagamento.' },
];

const PRODUCTS = [
  {
    id: 'it',
    flag: 'IT',
    title: 'Lettera di Intenti PDF',
    sub: 'Versione Italiana',
    desc: 'Modello professionale per la compravendita di velivolo tra privati in Italia. Pronto da compilare e stampare.',
  },
  {
    id: 'en',
    flag: 'EN',
    title: 'Letter of Intent PDF',
    sub: 'English Version',
    desc: 'Professional template for private aircraft sale agreements. Ready to fill in and print.',
  },
];

export default function LetteraIntenti() {
  const [modalLang, setModalLang] = useState(null); // 'it' | 'en' | null
  const [form, setForm] = useState({ nome: '', cognome: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    document.title = 'Lettera di Intenti per la Compravendita di Velivolo | AeroDeals';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'Lettera di Intenti per la compravendita di velivolo tra privati: modello professionale PDF (IT/EN) a €9.90. Tutela esclusiva, due diligence, prezzo e condizioni.');
  }, []);

  const openModal = (lang) => {
    setModalLang(lang);
    setOrderId(null);
    setForm({ nome: '', cognome: '', email: '' });
  };
  const closeModal = () => setModalLang(null);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.email) {
      toast.error('Compila tutti i campi.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/loi/order`, { ...form, language: modalLang });
      setOrderId(data.id);
      // Immediately trigger Stripe checkout for LOI
      const pkg = data.checkout_package_id || `loi_${modalLang}`;
      const { data: sess } = await axios.post(`${API}/checkout/session`, {
        package_id: pkg,
        origin_url: window.location.origin,
        loi_order_id: data.id,
      });
      if (sess?.url) {
        window.location.href = sess.url;
        return;
      }
      toast.error('Impossibile avviare il pagamento. Riprova.');
    } catch (err) {
      console.error(err);
      toast.error("Errore durante la registrazione dell'ordine. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const scrollToStore = () => document.getElementById('store')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="lettera-intenti-page">
      {/* ─── Hero ─── */}
      <section className="relative py-24 md:py-32 border-b border-zinc-800/50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#C5A059]/[0.08] via-transparent to-transparent pointer-events-none" />
        <div className="absolute right-[-10%] top-[20%] w-[500px] h-[500px] rounded-full bg-[#C5A059]/[0.05] blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.2fr,1fr] gap-12 items-center">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-4">Servizi · Lettera di Intenti</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white mb-6 leading-[1.05]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Lettera di Intenti<br /><span className="text-[#C5A059]">Compravendita Velivolo</span>
              </h1>
              <p className="text-base md:text-lg text-zinc-300 leading-relaxed mb-8 max-w-xl">
                Il documento per definire le intenzioni preliminari tra Venditore e Acquirente: acconto, prezzo,
                condizioni e modalità prima della definitiva compravendita privata di un velivolo.
              </p>
              <div className="flex flex-wrap gap-3 mb-10">
                <button
                  onClick={scrollToStore}
                  className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 font-medium text-sm hover:bg-[#D4B271] transition-colors"
                  data-testid="cta-hero-loi"
                >
                  Scarica la Lettera di Intenti PDF <ArrowRight size={14} />
                </button>
                <a
                  href="#cosa-include"
                  className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 px-6 py-3 font-medium text-sm hover:border-[#C5A059] hover:text-white transition-colors"
                >
                  Cosa include
                </a>
              </div>
              <div className="grid grid-cols-3 gap-4 max-w-lg border-t border-zinc-800 pt-6">
                <div className="flex items-start gap-2.5">
                  <Download size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">PDF</div>
                    <div className="text-[11px] text-zinc-500 leading-tight">Pronto all&apos;uso</div>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Mail size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">Via Email</div>
                    <div className="text-[11px] text-zinc-500 leading-tight">Consegna istantanea</div>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Award size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">IT + EN</div>
                    <div className="text-[11px] text-zinc-500 leading-tight">Due lingue</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Document preview card */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#C5A059]/15 to-transparent blur-2xl pointer-events-none" />
                <div className="relative bg-gradient-to-br from-zinc-900 to-[#0E0E10] border border-zinc-800 p-8">
                  <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#C5A059]/50 to-transparent" />
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-1">AeroDeals · LOI</div>
                      <div className="text-lg font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        Lettera di Intenti
                      </div>
                    </div>
                    <FileText size={24} className="text-[#C5A059]" />
                  </div>
                  {/* Mock doc lines */}
                  <div className="space-y-2.5 mb-6">
                    <div className="h-2 w-3/4 bg-zinc-800" />
                    <div className="h-2 w-full bg-zinc-800" />
                    <div className="h-2 w-5/6 bg-zinc-800" />
                    <div className="h-2 w-2/3 bg-zinc-800" />
                    <div className="h-2 w-full bg-zinc-800" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {['Parti', 'Velivolo', 'Prezzo', 'Acconto', 'Esclusiva', 'Condizioni'].map((t) => (
                      <div key={t} className="border border-zinc-800 px-3 py-2 text-xs text-zinc-400">{t}</div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-5 border-t border-zinc-800">
                    <div>
                      <div className="text-xl font-medium text-[#C5A059]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>€9,90</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-widest">PDF scaricabile</div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[#C5A059]">
                      <Download size={12} /> IT · EN
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Intro narrativa ─── */}
      <section id="cosa-include" className="py-20 md:py-24 border-b border-zinc-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-4">Il Documento</p>
          <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-10 max-w-3xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Blocca la vendita e tutelati con la Lettera di Intenti
          </h2>
          <div className="space-y-5 text-zinc-300 leading-relaxed text-[15px] max-w-3xl">
            <p>
              La Lettera di Intenti per la Compravendita di Velivolo è uno strumento essenziale nelle prime fasi di una
              trattativa di compravendita tra privati. Serve a formalizzare le intenzioni di Venditore ed Acquirente,
              garantendo l&apos;esclusiva e definendo in modo chiaro i termini principali dell&apos;operazione prima di procedere
              con le verifiche tecniche ed i passaggi di acquisizione definitivi.
            </p>
            <p>
              Questo documento consente di bloccare le condizioni economiche concordate, stabilire le modalità di
              pagamento e creare un quadro ordinato entro cui svolgere la due diligence. È particolarmente utile quando
              l&apos;acquirente intende sostenere costi di ispezione, consulenza o trasferte ed ha la necessità di avere un
              periodo di esclusiva sul velivolo.
            </p>
            <p>
              La Lettera di Intenti di AeroDeals è pensata per essere utilizzata in modo semplice ed immediato. È un
              modello professionale, chiaro ed adattabile a diverse tipologie di velivoli. Non sostituisce il contratto
              di compravendita, ma aiuta ad evitare incomprensioni e trattative non strutturate, riducendo il rischio di
              perdite di tempo e costi inutili.
            </p>
            <p>
              Il documento include le sezioni fondamentali per identificare le parti, descrivere il velivolo, definire
              il prezzo e disciplinare le condizioni preliminari. È in formato PDF scaricabile pronto per la stampa,
              ideale per chi desidera uno strumento pratico, professionale e immediatamente utilizzabile.
            </p>
          </div>
        </div>
      </section>

      {/* ─── Store (buy cards) ─── */}
      <section id="store" className="py-20 md:py-24 border-b border-zinc-800/50 bg-zinc-950/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Scarica</p>
          <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Scarica e stampa la Lettera di Intenti
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed mb-12 max-w-2xl">
            Definisci le condizioni dell&apos;accordo prima di procedere, blocca il velivolo e tutelati con un documento
            chiaro e professionale pronto all&apos;uso.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PRODUCTS.map((p) => (
              <div
                key={p.id}
                className="relative bg-gradient-to-br from-zinc-900 to-[#0E0E10] border border-zinc-800 p-8 flex flex-col hover:border-[#C5A059]/40 transition-colors"
                data-testid={`loi-product-${p.id}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#C5A059]/15">
                    <FileText size={24} className="text-[#C5A059]" />
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-widest bg-zinc-800 text-zinc-300 px-2.5 py-1">
                    {p.flag}
                  </span>
                </div>
                <h3 className="text-xl font-medium text-white mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{p.title}</h3>
                <p className="text-xs font-mono uppercase tracking-widest text-[#C5A059] mb-4">{p.sub}</p>
                <p className="text-sm text-zinc-400 leading-relaxed mb-6 flex-1">{p.desc}</p>
                <div className="flex items-end justify-between pt-5 border-t border-zinc-800">
                  <div>
                    <div className="text-3xl font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      €9<span className="text-xl align-top">,90</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 uppercase tracking-widest">PDF scaricabile</div>
                  </div>
                  <button
                    onClick={() => openModal(p.id)}
                    className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-5 py-2.5 font-medium text-sm hover:bg-[#D4B271] transition-colors"
                    data-testid={`buy-${p.id}-btn`}
                  >
                    Aggiungi al carrello <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-xs text-zinc-500 leading-relaxed max-w-3xl">
            Dopo il pagamento riceverai il PDF all&apos;indirizzo email indicato. Il documento non sostituisce il contratto
            di compravendita; per consulenze personalizzate visita la pagina{' '}
            <a href="/consulenze-compravendita-velivoli" className="text-[#C5A059] hover:underline">Consulenze</a>.
          </p>
        </div>
      </section>

      {/* ─── Benefits ─── */}
      <section className="py-20 md:py-24 border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Perché è utile</p>
          <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-12 max-w-2xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Sei vantaggi chiari, un documento professionale
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <div key={b.title} className="flex gap-4 bg-zinc-900/40 border border-zinc-800 p-6 hover:border-[#C5A059]/40 transition-colors" data-testid={`loi-benefit-${i}`}>
                <div className="w-11 h-11 flex items-center justify-center bg-[#C5A059]/15 shrink-0">
                  <b.icon size={20} className="text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="text-base font-medium text-white mb-1.5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{b.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Outro ─── */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <blockquote className="border-l-2 border-[#C5A059] pl-6 text-zinc-300 leading-relaxed">
            <strong className="text-white">Scopri tutti i velivoli attualmente disponibili</strong> visitando la nostra pagina{' '}
            <a href="/listings" className="text-[#C5A059] hover:underline">Annunci</a>. Trova facilmente il velivolo
            più adatto alle tue esigenze, messaggia con il venditore e concludi l&apos;affare con AeroDeals.
          </blockquote>
        </div>
      </section>

      {/* ─── Purchase Modal ─── */}
      {modalLang && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          onClick={closeModal}
          data-testid="loi-modal"
        >
          <div
            className="relative bg-zinc-950 border border-zinc-800 w-full max-w-md p-7 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
              aria-label="Chiudi"
              data-testid="loi-modal-close"
            >
              <X size={18} />
            </button>

            {orderId ? (
              <div className="text-center py-4">
                <CheckCircle2 size={42} className="text-[#C5A059] mx-auto mb-4" />
                <h3 className="text-xl font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Ordine registrato
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-6">
                  Grazie! Ti invieremo un&apos;email con il link di pagamento. Subito dopo la conferma riceverai il PDF
                  all&apos;indirizzo indicato.
                </p>
                <div className="bg-zinc-900 border border-zinc-800 p-3 text-[11px] font-mono text-zinc-500 mb-5">
                  ORDER #{orderId.slice(0, 8).toUpperCase()}
                </div>
                <button
                  onClick={closeModal}
                  className="text-xs font-mono uppercase tracking-widest text-[#C5A059] hover:text-[#D4B271] transition-colors"
                >
                  Chiudi
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#C5A059]/15">
                    <FileText size={18} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-[#C5A059]">Checkout · LOI {modalLang.toUpperCase()}</div>
                    <div className="text-sm text-zinc-400">Prezzo: €9,90</div>
                  </div>
                </div>
                <h3 className="text-xl font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  I tuoi dati per la fatturazione
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-6">
                  Inserisci i tuoi dati: dopo il pagamento il PDF verrà inviato all&apos;email indicata.
                </p>
                <form onSubmit={submit} className="space-y-4" data-testid="loi-order-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5">Nome *</label>
                      <input type="text" value={form.nome} onChange={update('nome')} required className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="loi-nome" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5">Cognome *</label>
                      <input type="text" value={form.cognome} onChange={update('cognome')} required className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="loi-cognome" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5">Email *</label>
                    <input type="email" value={form.email} onChange={update('email')} required placeholder="tu@email.com" className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-[#C5A059] transition-colors" data-testid="loi-email" />
                  </div>

                  <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
                    <span className="text-sm text-zinc-400">Totale</span>
                    <span className="text-xl font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>€9,90</span>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#C5A059] text-black px-6 py-3 font-medium text-sm hover:bg-[#D4B271] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="loi-submit"
                  >
                    {loading ? 'Registrazione...' : 'Procedi al pagamento'}
                    {!loading && <ArrowRight size={14} />}
                  </button>

                  <p className="text-[11px] text-zinc-500 leading-relaxed text-center">
                    Pagamento online in attivazione. Ti invieremo il link di pagamento per email.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
